/**
 * Merchant command palette — Ctrl-K / Cmd-K from anywhere in the portal.
 *
 * UI_CONSOLIDATION_PLAN 4C. The admin panel has had one of these for a while
 * (`components/admin/CommandPalette.jsx`); this is the same pattern, the same
 * keyboard contract and — by importing its stylesheet rather than copying it —
 * literally the same styling. Two palettes that look different would read as
 * two products.
 *
 * What it can do:
 *   • jump to any page or hub TAB this business can actually open (the gates
 *     come from `navTargets.js`, which resolves them with the same helpers the
 *     Sidebar and the hub containers use — so it can never offer a dead end);
 *   • search this merchant's own items, customers and orders, through
 *     endpoints that ALREADY EXIST. No API was added for this screen.
 *
 * 🔴 The remote searches are gated too, and for a plain reason: `/api/customers/`
 * answers for anybody authenticated, so a business without the Customers tool
 * would get rows it has no screen to open them in. We ask the same question the
 * nav asks before we ask the server.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Package, ShoppingCart, Search, Users, X,
} from 'lucide-react';

import AuthContext from '../../context/AuthContext';
import API_BASE_URL from '../../config/api';
import { usePluginContext } from '../../context/PluginContext';
import usePlatformAdmin from '../../hooks/usePlatformAdmin';
import { devLog } from '../../utils/devLog';
import {
  capWord, isToolOn, hasCatalogSchema, configVersion, subscribeBusinessConfig,
  isSiteOnly,
} from '../../utils/businessConfig';
import { buildNavTargets } from './navTargets';
import '../admin/CommandPalette.css';

const isMac = () => {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || '');
};

/** Same scorer as the admin palette: exact > prefix > substring > subsequence. */
const fuzzy = (haystack, needle) => {
  if (!needle) return { match: true, score: 0 };
  if (!haystack) return { match: false, score: 0 };
  const h = String(haystack).toLowerCase();
  const n = String(needle).toLowerCase();
  if (h === n) return { match: true, score: 100 };
  if (h.startsWith(n)) return { match: true, score: 80 };
  if (h.includes(n)) return { match: true, score: 60 };
  let idx = 0;
  for (let i = 0; i < h.length && idx < n.length; i += 1) {
    if (h[i] === n[idx]) idx += 1;
  }
  return idx === n.length ? { match: true, score: 30 } : { match: false, score: 0 };
};

const scoreTarget = (t, q) => {
  const label = fuzzy(t.label, q);
  if (label.match) return label.score * 3;
  const group = fuzzy(t.group, q);
  if (group.match) return group.score * 1.2;
  const kw = fuzzy(t.keywords, q);
  if (kw.match) return kw.score;
  return 0;
};

const CommandPalette = () => {
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);
  const { isPluginEnabled } = usePluginContext();
  const isPlatformAdmin = usePlatformAdmin();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [remote, setRemote] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Tools switch in a module-level config, not React state. Same subscription
  // the Sidebar uses, so a feature turned on is jumpable to immediately.
  const [cfgVersion, setCfgVersion] = useState(configVersion);
  useEffect(() => {
    if (configVersion() !== cfgVersion) setCfgVersion(configVersion());
    const unsubscribe = subscribeBusinessConfig(setCfgVersion);
    const onToolsChanged = () => setCfgVersion(configVersion());
    window.addEventListener('inikru:tools-changed', onToolsChanged);
    return () => {
      unsubscribe();
      window.removeEventListener('inikru:tools-changed', onToolsChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authAxios = useMemo(
    () => axios.create({
      baseURL: `${API_BASE_URL}/api`,
      headers: { Authorization: `Bearer ${authToken?.access}` },
    }),
    [authToken?.access]
  );

  const targets = useMemo(
    () => buildNavTargets({ isPluginEnabled, isPlatformAdmin }),
    // cfgVersion is a real dependency: the gates read the module config.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPluginEnabled, isPlatformAdmin, cfgVersion]
  );

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 140);
    return () => clearTimeout(t);
  }, [query]);

  // Ctrl-K / Cmd-K toggles, Esc closes.
  useEffect(() => {
    const handler = (e) => {
      const mod = isMac() ? e.metaKey : e.ctrlKey;
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // An external opener (a top-bar button, a mobile tap) without a second copy
  // of this component's state.
  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener('inikru:open-command-palette', openIt);
    return () => window.removeEventListener('inikru:open-command-palette', openIt);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebounced('');
    setHighlight(0);
    setRemote([]);
    const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  // ── Remote search over endpoints that already exist ───────────────────────
  useEffect(() => {
    const q = debounced.trim();
    if (!open || q.length < 2 || !authToken?.access) {
      setRemote([]);
      return undefined;
    }
    let cancelled = false;
    const jobs = [];

    // A site-only (portfolio) account has no products, customers or orders to
    // search — its palette is navigation only.
    const searchCommerce = !isSiteOnly();

    if (searchCommerce) jobs.push(
      authAxios.get('/order-book/products/search/', { params: { q } })
        .then((res) => (res.data || []).slice(0, 5).map((p) => ({
          type: 'product',
          label: p.name,
          hint: [p.category, p.price ? `₹${p.price}` : ''].filter(Boolean).join(' · '),
          // Which edit form a business has is decided by ONE question — the
          // same one App.js's CatalogEntry asks — so the palette cannot send a
          // schema business to the bespoke garment form or the reverse.
          path: hasCatalogSchema() ? `/catalog/${p.id}/edit` : `/EditProduct/${p.id}`,
        })))
        .catch(() => [])
    );

    if (searchCommerce && isToolOn('customers')) {
      jobs.push(
        authAxios.get('/customers/', { params: { search: q } })
          .then((res) => {
            const rows = res.data?.customers || res.data?.results || res.data || [];
            return (Array.isArray(rows) ? rows : []).slice(0, 5).map((c) => ({
              type: 'customer',
              label: c.name || c.phone || 'Customer',
              hint: [c.phone, c.email].filter(Boolean).join(' · '),
              path: `/customers/${c.id}`,
            }));
          })
          .catch(() => [])
      );
    }

    if (searchCommerce) jobs.push(
      authAxios.get('/unified-orders/', { params: { search: q, page_size: 5 } })
        .then((res) => {
          const rows = res.data?.results || res.data?.orders || [];
          return (Array.isArray(rows) ? rows : []).slice(0, 5).map((o) => {
            // An ONLINE order's `order_number` is its UUID — unreadable in a
            // one-line row, so the customer's name leads and the id is trimmed
            // to the prefix a person can actually match against.
            const num = String(o.order_number || o.id);
            const short = num.length > 12 ? `#${num.slice(0, 8)}` : `#${num}`;
            return {
              type: 'order',
              label: o.customer_name || short,
              hint: [short, o.total_amount ? `₹${o.total_amount}` : '', o.payment_status]
                .filter(Boolean).join(' · '),
              // Land where that order is EDITABLE, not on the merged inbox —
              // which is plugin-gated and may not be switched on at all.
              path: o.kind === 'offline' ? '/orderbook' : '/Orders',
            };
          });
        })
        .catch(() => [])
    );

    Promise.all(jobs)
      .then((sets) => { if (!cancelled) setRemote(sets.flat()); })
      .catch((err) => devLog.warn('command palette search failed', err));

    return () => { cancelled = true; };
  }, [debounced, open, authAxios, authToken?.access, cfgVersion]);

  const results = useMemo(() => {
    const q = debounced.trim();
    const items = [];
    if (!q) {
      // No query: the shortcuts worth having on screen, not all 60 targets.
      targets.slice(0, 8).forEach((t) => items.push({ ...t, type: 'target', _score: 0 }));
    } else {
      targets.forEach((t) => {
        const score = scoreTarget(t, q);
        if (score > 0) items.push({ ...t, type: 'target', _score: score });
      });
      remote.forEach((r) => items.push({ ...r, _score: 55 }));
    }
    items.sort((a, b) => b._score - a._score);
    return items.slice(0, 30);
  }, [debounced, targets, remote]);

  useEffect(() => { setHighlight(0); }, [results.length, debounced]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${highlight}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const close = useCallback(() => setOpen(false), []);

  const activate = useCallback((item) => {
    if (!item || !item.path) return;
    navigate(item.path);
    close();
  }, [navigate, close]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(results.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(results[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  if (!open) return null;

  const shortcutLabel = isMac() ? 'Cmd K' : 'Ctrl K';
  const ROW_ICON = {
    product: Package,
    customer: Users,
    order: ShoppingCart,
    target: ArrowRight,
  };
  const PILL = {
    product: capWord('product', 'Product'),
    customer: capWord('customer', 'Customer'),
    order: capWord('order', 'Order'),
  };

  return (
    <div className="cmd-palette" role="dialog" aria-modal="true" aria-label="Command palette">
      <div className="cmd-palette__backdrop" onClick={close} aria-hidden="true" />
      <div className="cmd-palette__panel">
        <div className="cmd-palette__input-row">
          <Search size={16} className="cmd-palette__input-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            className="cmd-palette__input"
            placeholder={`Jump to a screen, or search your ${capWord('products', 'products').toLowerCase()}, ${capWord('customer', 'customer').toLowerCase()}s and ${capWord('orders', 'orders').toLowerCase()}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmd-palette-results"
            aria-activedescendant={results[highlight] ? `cmd-opt-${highlight}` : undefined}
            aria-autocomplete="list"
          />
          <span className="cmd-palette__shortcut" aria-hidden="true">{shortcutLabel}</span>
          <button
            type="button"
            className="cmd-palette__close"
            onClick={close}
            aria-label="Close command palette"
          >
            <X size={14} />
          </button>
        </div>

        <div
          className="cmd-palette__results"
          id="cmd-palette-results"
          role="listbox"
          ref={listRef}
        >
          {results.length === 0 ? (
            <div className="cmd-palette__empty">No matches.</div>
          ) : (
            results.map((item, idx) => {
              const Icon = ROW_ICON[item.type] || ArrowRight;
              const active = idx === highlight;
              return (
                <div
                  key={`${item.type}-${item.path}-${idx}`}
                  id={`cmd-opt-${idx}`}
                  role="option"
                  aria-selected={active}
                  data-cmd-idx={idx}
                  className={`cmd-palette__row ${active ? 'is-active' : ''}`}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => activate(item)}
                >
                  <span className="cmd-palette__row-icon"><Icon size={16} /></span>
                  <div className="cmd-palette__row-text">
                    <div className="cmd-palette__row-label">{item.label}</div>
                    {item.hint && <div className="cmd-palette__row-hint">{item.hint}</div>}
                  </div>
                  {item.type === 'target' ? (
                    <span className="cmd-palette__row-pill">{item.group}</span>
                  ) : (
                    <span className="cmd-palette__row-pill cmd-palette__row-pill--alt">
                      {PILL[item.type]}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="cmd-palette__footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
